<template>
  <article :id="`plan-${catalog.id}`" class="plan-block" :class="{ 'plan-block--reverse': reverse }">
    <div class="plan-block__media">
      <img
        v-if="image"
        :src="image"
        :alt="`Plan ${catalog.name}`"
        class="plan-block__image"
        :class="{ 'plan-block__image--flip': catalog.id === 'plus' }"
      />
      <div v-else class="plan-block__media-fallback" aria-hidden="true">
        <i class="fas fa-briefcase"></i>
        <span>Beneficios médicos para tu equipo</span>
      </div>
    </div>

    <div class="plan-block__body">
      <span v-if="catalog.badge" class="plan-block__badge">{{ catalog.badge }}</span>
      <h3 class="plan-block__name">{{ catalog.name }}</h3>
      <p class="plan-block__for">{{ catalog.for }}</p>

      <div class="plan-block__pricing">
        <div class="plan-block__price-row">
          <transition name="price-swap" mode="out-in">
            <span :key="offer.amount + offer.suffix" class="plan-block__price">${{ formatPrice(offer.amount) }}<small>{{ offer.suffix }}</small></span>
          </transition>
          <s v-if="offer.was" class="plan-block__was">${{ formatPrice(offer.was) }}</s>
        </div>
        <p v-if="sizeLine" class="plan-block__size">{{ sizeLine }}</p>
        <transition name="price-swap" mode="out-in">
          <p v-if="offer.savings" :key="offer.savings" class="plan-block__savings">
            <i class="fas fa-tag" aria-hidden="true"></i>
            {{ offer.savings }}
          </p>
        </transition>
      </div>

      <div class="plan-block__controls">
        <div v-if="offer.intervals.length > 1" class="plan-block__segmented" role="group" :aria-label="`Forma de pago del plan ${catalog.name}`">
          <button
            v-for="option in offer.intervals"
            :key="option.value"
            type="button"
            :aria-pressed="String(offer.interval === option.value)"
            @click="interval = option.value"
          >
            {{ option.name }}
          </button>
        </div>

        <div v-if="catalog.fam" class="plan-block__stepper" role="group" aria-label="Personas en el plan Familiar">
          <button type="button" aria-label="Quitar una persona" :disabled="offer.people <= minPeople" @click="people = offer.people - 1">
            <i class="fas fa-minus" aria-hidden="true"></i>
          </button>
          <span aria-live="polite">{{ offer.people }} personas</span>
          <button type="button" aria-label="Agregar una persona" :disabled="offer.people >= maxPeople" @click="people = offer.people + 1">
            <i class="fas fa-plus" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <p v-if="offer.interval !== 'month' && offer.prepaidWellness" class="plan-block__note">
        Pagando {{ offer.interval === 'year' ? 'anual' : 'trimestral' }}, tus exámenes del Wellness están disponibles desde el día 1.
      </p>

      <ul class="plan-block__benefits">
        <li v-for="[title, detail] in catalog.benefits" :key="title">
          <i class="fas fa-check" aria-hidden="true"></i>
          <div>
            <strong>{{ title }}</strong>
            <span>{{ detail }}</span>
          </div>
        </li>
      </ul>

      <button type="button" class="plan-block__cta" :disabled="!route" @click="buy">
        Adquirir plan {{ catalog.name }}
        <i class="fas fa-arrow-right ml-2" aria-hidden="true"></i>
      </button>
    </div>
  </article>
</template>

<script>
import { FAMILY_MAX_PEOPLE } from '@/core/utils/membershipFamily'
import { formatPrice, planOffer, purchaseRoute } from '../../data/planOffer'

const FAMILY_DEFAULT_PEOPLE = 4
const COMPANY_DEFAULT_MIN = 5

export default {
  name: 'PlanDetailBlock',
  props: {
    catalog: { type: Object, required: true },
    apiPlan: { type: Object, default: null },
    image: { type: String, default: null },
    reverse: { type: Boolean, default: false }
  },
  data() {
    return {
      interval: 'month',
      people: FAMILY_DEFAULT_PEOPLE,
      minPeople: 2,
      maxPeople: FAMILY_MAX_PEOPLE
    }
  },
  computed: {
    offer() {
      return planOffer(this.catalog, this.apiPlan, { interval: this.interval, people: this.people })
    },
    sizeLine() {
      if (this.catalog.fam) return `$${this.offer.perPerson} por persona`
      if (this.offer.unit) {
        const min = Number(this.apiPlan && this.apiPlan.min_members) || COMPANY_DEFAULT_MIN
        return `Por empleado · mínimo ${min} empleados`
      }
      return ''
    },
    route() {
      return purchaseRoute(this.apiPlan, { interval: this.offer.interval, people: this.offer.people }, !!this.catalog.fam)
    }
  },
  methods: {
    formatPrice,
    buy() {
      if (this.route) this.$router.push(this.route).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';

.plan-block {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 3rem;
  align-items: start;
  padding: 3.5rem 0;
  border-bottom: 1px solid $landing-border-light;
  scroll-margin-top: 90px;

  &:last-child {
    border-bottom: 0;
  }

  &--reverse {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);

    .plan-block__media {
      order: 2;
    }
  }
}

.plan-block__media {
  position: sticky;
  top: 110px;
}

.plan-block__image,
.plan-block__media-fallback {
  width: 100%;
  aspect-ratio: 4 / 5;
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(13, 43, 69, 0.14);
}

.plan-block__image {
  display: block;
  object-fit: cover;
  /* Las fotos son más altas que el recuadro: anclar arriba para no cortar las caras. */
  object-position: center top;
}

.plan-block__image--flip {
  transform: scaleX(-1);
}

.plan-block__media-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 2rem;
  background: $landing-primary-gradient-dark;
  color: white;
  text-align: center;
  font-size: 1.3rem;
  font-weight: 600;

  i {
    font-size: 3.5rem;
    color: $landing-secondary-color;
  }
}

.plan-block__badge {
  display: inline-block;
  margin-bottom: 0.75rem;
  padding: 4px 12px;
  border-radius: 999px;
  background: $landing-secondary-color;
  color: $landing-primary-color;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.plan-block__name {
  margin: 0 0 0.5rem;
  color: $landing-primary-color;
  font-size: clamp(1.9rem, 3.5vw, 2.6rem);
  font-weight: 700;
}

.plan-block__for {
  margin: 0 0 1.5rem;
  color: $landing-text-medium;
  font-size: 1.1rem;
  line-height: 1.55;
  max-width: 38em;
}

.plan-block__pricing {
  margin-bottom: 1.25rem;
}

.plan-block__price-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.plan-block__price {
  color: $landing-primary-color;
  font-size: clamp(2.6rem, 5vw, 3.4rem);
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;

  small {
    margin-left: 4px;
    color: $landing-text-medium;
    font-size: 1.1rem;
    font-weight: 500;
  }
}

.plan-block__was {
  color: $landing-text-medium;
  font-size: 1.3rem;
}

.plan-block__size {
  margin: 0.5rem 0 0;
  color: $landing-text-medium;
  font-size: 1rem;
}

.plan-block__savings {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0.75rem 0 0;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(34, 207, 224, 0.14);
  color: $landing-accent-text;
  font-size: 0.98rem;
  font-weight: 600;
}

.plan-block__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.plan-block__segmented,
.plan-block__stepper {
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border: 1px solid $landing-border-light;
  border-radius: 999px;
  background: $landing-bg-light;
}

.plan-block__segmented button {
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: $landing-text-medium;
  font-weight: 600;
  transition: background-color 0.2s ease, color 0.2s ease;

  &[aria-pressed='true'] {
    background: $landing-primary-color;
    color: white;
  }

  &:focus-visible {
    outline: 2px solid $landing-secondary-color;
    outline-offset: 2px;
  }
}

.plan-block__stepper {
  gap: 0.5rem;

  span {
    min-width: 6.5em;
    color: $landing-primary-color;
    font-weight: 700;
    text-align: center;
  }

  button {
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 50%;
    background: white;
    color: $landing-primary-color;
    box-shadow: 0 1px 3px rgba(13, 43, 69, 0.15);

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:focus-visible {
      outline: 2px solid $landing-secondary-color;
      outline-offset: 2px;
    }
  }
}

.plan-block__note {
  margin: 0 0 1rem;
  color: $landing-accent-text;
  font-size: 0.95rem;
}

.plan-block__benefits {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.1rem 1.75rem;
  margin: 1.25rem 0 2rem;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 0.75rem;
  }

  i {
    flex-shrink: 0;
    margin-top: 4px;
    color: $landing-secondary-color;
  }

  strong {
    display: block;
    color: $landing-primary-color;
    font-size: 1.02rem;
  }

  span {
    color: $landing-text-medium;
    font-size: 0.95rem;
    line-height: 1.45;
  }
}

.plan-block__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 0 2rem;
  border: 0;
  border-radius: 10px;
  background: $landing-primary-color;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background: $landing-primary-dark;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid $landing-secondary-color;
    outline-offset: 2px;
  }
}

.price-swap-enter-active,
.price-swap-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.price-swap-enter,
.price-swap-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (prefers-reduced-motion: reduce) {
  .price-swap-enter-active,
  .price-swap-leave-active {
    transition: none;
  }
}

@media (max-width: 991px) {
  .plan-block,
  .plan-block--reverse {
    grid-template-columns: 1fr;
    gap: 1.75rem;
    padding: 2.5rem 0;

    .plan-block__media {
      order: 0;
      position: static;
    }
  }

  .plan-block__image,
  .plan-block__media-fallback {
    aspect-ratio: 4 / 5;
    max-height: 460px;
  }

  .plan-block__image {
    object-position: center top;
  }
}

@media (max-width: 575px) {
  .plan-block__benefits {
    grid-template-columns: 1fr;
  }

  .plan-block__cta {
    width: 100%;
  }

  .plan-block__segmented {
    width: 100%;

    button {
      flex: 1;
      padding: 0 8px;
    }
  }
}
</style>
